/**
 * BrokerSection Component
 *
 * Single-broker section presented as a "button + capture modal", mirroring
 * the Collateral / Stamp Duty sections for visual consistency:
 *
 *  - When no broker is selected the section header shows "Broker (0)" and
 *    an "Add Broker" button; below is an empty-state Paper.
 *  - When a broker is selected the header shows "Broker (1)", a single-row
 *    table renders the broker / fee % / computed amount with edit + remove
 *    actions. The Add button is hidden (single-broker constraint).
 *
 * The broker FEE is wired to reference data: the modal has a "Broker Fee"
 * dropdown listing the `Broker Fee`-type rows from Reference Data → Placement
 * Fees. Selecting a row sets placement_fees_id (whose GL expense/control
 * accounts drive the commission journal export) and pre-fills the % field,
 * which the user can then override. Broker fee is an upfront, one-off fee =
 * capital_amount × %.
 *
 * The form fields broker_id / broker_fees / placement_fees_id remain
 * authoritative — the modal just writes into them via setValue. is_brokered is
 * kept in sync by an effect on broker_id presence so validation in
 * useContractValidation.js continues to apply.
 *
 * Stamp duty is NOT handled here (T112): it is a standalone option captured by
 * StampDutySection.
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useFormContext } from 'react-hook-form';
import { useContractFormContext } from '../ContractFormContext';
import StakeholderAutocomplete from '../../../common/StakeholderAutocomplete';
import ConfirmDialog from '../../../common/ConfirmDialog';
import { formatNumber, formatRatePct } from '../../../../utils/formatters';

const EMPTY_FORM = {
  broker_id: '',
  placement_fees_id: '',
  broker_fees: '',
};

// Fixed widths so the fee% + Amount columns sit tight together and Actions
// anchors at the same right-edge position as the Stamp Duty / Collateral tables.
const PCT_COL_W = 120;
const AMOUNT_COL_W = 150;
const ACTIONS_COL_W = 96;

function BrokerSection() {
  const { watch, setValue } = useFormContext();
  const { refData, appendToRefData, initialData, isEditable } = useContractFormContext();

  const brokerId = watch('broker_id');
  const brokerFees = watch('broker_fees');
  const placementFeesId = watch('placement_fees_id');
  const isBrokered = !!brokerId;
  const disabled = isEditable === false;

  // Contract capital + currency for the computed (capital × %) amount.
  const capitalAmount = parseFloat(watch('capital_amount')) || 0;
  const investmentCurrencyId = watch('investment_currency_id');
  const currencyCode = (refData.currencies || []).find(c => c.id === investmentCurrencyId)?.currency_code || '';

  // Available Broker Fee reference rows (type = 'Broker Fee', active).
  const brokerFeeOptions = (refData.placementFees || []).filter(
    (f) => f.type === 'Broker Fee' && f.active !== false
  );

  // Sync is_brokered with broker_id presence. Validation in
  // useContractValidation.js still keys off is_brokered.
  useEffect(() => {
    setValue('is_brokered', !!brokerId);
  }, [brokerId, setValue]);

  // Dialog state — separate from the live form so cancel doesn't mutate it.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY_FORM });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, onConfirm: null });

  const computeAmount = (pct) => {
    const p = parseFloat(pct);
    if (!isFinite(p)) return null;
    return capitalAmount * p / 100;
  };

  const handleOpenDialog = (mode) => {
    if (mode === 'edit') {
      // Seed placement_fees_id from the form; fall back to matching the saved
      // % to a Broker Fee row so an edit still pre-selects the dropdown.
      let pfId = placementFeesId || '';
      if (!pfId && brokerFees !== '' && brokerFees != null) {
        const match = brokerFeeOptions.find(
          (o) => Math.abs(parseFloat(o.percentage) - parseFloat(brokerFees)) < 0.00005
        );
        if (match) pfId = match.id;
      }
      setDraft({
        broker_id: brokerId || '',
        placement_fees_id: pfId,
        // Normalize to a trimmed value so the field shows 1.5 not 1.5000.
        broker_fees: formatRatePct(brokerFees),
      });
    } else {
      setDraft({ ...EMPTY_FORM });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDraft({ ...EMPTY_FORM });
  };

  // Selecting a Broker Fee row sets the ref id and (re)pre-fills the % from that
  // row. Choosing a different row is a deliberate new selection, so it always
  // refreshes the %; the user can then override by typing in the % field after.
  const handleSelectFee = (feeId) => {
    const selected = brokerFeeOptions.find((o) => o.id === feeId);
    setDraft((prev) => ({
      ...prev,
      placement_fees_id: feeId,
      broker_fees: selected ? parseFloat(selected.percentage) : prev.broker_fees,
    }));
  };

  const isSubmitDisabled = () => {
    if (!draft.broker_id) return true;
    if (!draft.placement_fees_id) return true;
    const fees = parseFloat(draft.broker_fees);
    if (!isFinite(fees) || fees < 0) return true;
    return false;
  };

  const handleSubmit = () => {
    if (isSubmitDisabled()) return;
    setValue('broker_id', parseInt(draft.broker_id, 10));
    setValue('placement_fees_id', parseInt(draft.placement_fees_id, 10));
    setValue('broker_fees', parseFloat(draft.broker_fees));
    handleCloseDialog();
  };

  const handleRemove = () => {
    setConfirmDialog({
      open: true,
      onConfirm: () => {
        setValue('broker_id', '');
        setValue('placement_fees_id', '');
        setValue('broker_fees', '');
        setConfirmDialog({ open: false, onConfirm: null });
      },
    });
  };

  const brokerName = (refData.brokers || []).find(b => b.id === brokerId)?.name
    || initialData?.broker_name
    || '—';

  const draftFeeRow = brokerFeeOptions.find((o) => o.id === draft.placement_fees_id);
  const rowAmount = computeAmount(brokerFees);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon color="primary" />
          <Typography variant="h6">
            Broker ({isBrokered ? 1 : 0})
          </Typography>
        </Box>
        {!disabled && !isBrokered && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('add')}
          >
            Add Broker
          </Button>
        )}
      </Box>

      {!isBrokered ? (
        <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
          <Typography color="textSecondary">
            No broker on this contract. Click "Add Broker" to record one.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            {/* 8-column grid mirroring the Collateral table so Broker Fee %,
                Amount and Actions land in the same positions across the cards.
                Columns 2–4 and 7 are blank fillers. */}
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Broker</TableCell>
                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', width: PCT_COL_W }}>Broker Fee %</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', width: AMOUNT_COL_W }}>Amount</TableCell>
                {!disabled && <TableCell align="center" sx={{ width: ACTIONS_COL_W, minWidth: ACTIONS_COL_W }}>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body2" fontWeight="medium">{brokerName}</Typography>
                  </Box>
                </TableCell>
                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell align="right" sx={{ width: PCT_COL_W }}>
                  <Typography variant="body2">
                    {brokerFees !== '' && brokerFees != null ? `${formatRatePct(brokerFees)}%` : '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ width: AMOUNT_COL_W }}>
                  <Typography variant="body2">
                    {rowAmount != null ? `${currencyCode} ${formatNumber(rowAmount)}` : '—'}
                  </Typography>
                </TableCell>
                {!disabled && (
                  <TableCell align="center" sx={{ width: ACTIONS_COL_W, minWidth: ACTIONS_COL_W }}>
                    <Tooltip title="Edit broker">
                      <IconButton size="small" onClick={() => handleOpenDialog('edit')}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove broker">
                      <IconButton size="small" color="error" onClick={handleRemove}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{brokerId ? 'Edit Broker' : 'Add Broker'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid size={12}>
              <StakeholderAutocomplete
                stakeholderType="broker"
                options={refData.brokers}
                value={draft.broker_id}
                onChange={(id) => setDraft(prev => ({ ...prev, broker_id: id }))}
                onStakeholderCreated={(newBroker) => {
                  appendToRefData('brokers', newBroker);
                  setDraft(prev => ({ ...prev, broker_id: newBroker.id }));
                }}
                label="Broker *"
                countries={refData.currencies}
              />
            </Grid>

            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel id="broker-fee-ref-label">Broker Fee *</InputLabel>
                <Select
                  labelId="broker-fee-ref-label"
                  label="Broker Fee *"
                  value={draft.placement_fees_id}
                  onChange={(e) => handleSelectFee(e.target.value)}
                >
                  {brokerFeeOptions.length === 0 && (
                    <MenuItem value="" disabled>
                      No broker-fee rates in Reference Data → Placement Fees
                    </MenuItem>
                  )}
                  {brokerFeeOptions.map((o) => (
                    <MenuItem key={o.id} value={o.id}>
                      {o.reference} — {formatRatePct(o.percentage)}%
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={12}>
              <TextField
                label="Broker Fees (%) *"
                type="number"
                fullWidth
                value={draft.broker_fees}
                onChange={(e) => setDraft(prev => ({ ...prev, broker_fees: e.target.value }))}
                inputProps={{ step: 0.0001, min: 0 }}
                helperText={
                  draftFeeRow
                    ? `Pre-filled from reference data (${formatRatePct(draftFeeRow.percentage)}%) — editable`
                    : 'Pre-filled from reference data when a broker fee is selected — editable'
                }
              />
            </Grid>

            {draft.placement_fees_id && computeAmount(draft.broker_fees) != null && (
              <Grid size={12}>
                <Typography variant="body2" color="text.secondary">
                  Amount (capital × %): <strong>{currencyCode} {formatNumber(computeAmount(draft.broker_fees))}</strong>
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={isSubmitDisabled()}>
            {isBrokered ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmDialog.open}
        title="Remove Broker"
        message="Are you sure you want to remove the broker? Broker fee will be cleared."
        confirmText="Remove"
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ open: false, onConfirm: null })}
      />
    </Box>
  );
}

export default BrokerSection;
